import { useState } from 'react';
import { useMount } from 'react-use';

import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import { useNamedLogger } from '@eluve/logger';
import {
  useTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';
import {
  AppointmentUserMicSettings,
  MicrophoneRequestAlert,
  useSelectedMicrophoneDevice,
} from '@eluve/smart-blocks';

import {
  useTranscriptionMachineActor,
  useTranscriptionMachineSelector,
} from '../hooks/useTranscriptionMachine';

const InnerMicrophoneFailedDialog = () => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(true);
  const [selectedMicId, setSelectedMicId] = useSelectedMicrophoneDevice();
  const { send } = useTranscriptionMachineActor();
  const logger = useNamedLogger('MicrophoneFailedDialog');
  const tenantId = useTenantIdFromParams();
  const userId = useUserIdFromSession();

  const machineState = useTranscriptionMachineSelector((state) => state);
  const { microphoneStatus, sessionAppointmentId: appointmentId } =
    machineState.context;

  useMount(() => {
    logger.warn('MicrophoneFailedDialog shown', {
      microphoneStatus,
      appointmentId,
      userId,
      tenantId,
    });
  });

  const onResume = () => {
    send({ type: 'RESUME', deviceId: selectedMicId ?? undefined });
  };

  const onClose = () => {
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent showCloseButton={false}>
        <DialogHeader className="mb-2 space-y-4">
          <DialogTitle>{t('appointmentPage.micFailed.title')}</DialogTitle>
          <DialogDescription>
            {t('appointmentPage.micFailed.description')}
          </DialogDescription>
          <MicrophoneRequestAlert />
          <AppointmentUserMicSettings
            forceVerticalLayout
            isCompact
            selectedMicId={selectedMicId}
            setSelectedMicId={setSelectedMicId}
            showInputLabel={false}
          />
        </DialogHeader>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            {t('appointmentPage.micFailed.stayPaused')}
          </Button>
          <Button disabled={!selectedMicId} onClick={onResume}>
            {t('appointment.active.resumeRecording')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const MicrophoneFailedDialog = () => {
  const machineState = useTranscriptionMachineSelector((state) => state);
  const hasFailed =
    machineState.can({ type: 'RESUME' }) &&
    machineState.context.microphoneStatus === 'disconnected';

  if (!hasFailed) {
    return null;
  }

  return <InnerMicrophoneFailedDialog />;
};
